/**
 * How the Results split is built, once, in plain words with every number from the report (round-4 G1-N3/G1-N4): the share price leg from the two prices, the two share counts and the
 * today's-dollars factor; the cash leg and the look-through leg as the printed parts; and the sum. "=" only when the printed pieces reproduce the printed result at the cent, else "≈".
 */
import type { RoundReport } from "@bsim/contracts";
import { formatMetric } from "@gxs/metric-defs";
import { formatSharesM, formatUsdPerShare } from "../format";
import { printedParts } from "./labels";
import { READS_ON, SPLIT_LEG } from "./splitWords";

/** Under half a dollar the price gap is too small to read the factor back from the legs. */
const MIN_PRICE_GAP_USD = 0.5;

const cents = (v: number): number => Math.round(Number((v * 100).toPrecision(12)));

/** The factor that turns year-end dollars into today's dollars: the report's own when it carries one, else read back from the price leg; null when the price gap is too small to read it. */
function todayFactor(report: Pick<RoundReport, "attribution" | "score">, price: number | undefined, shareRatio: number): { readonly k: number; readonly twinPrice: number } | null {
  const c = report.score.components;
  if (c === undefined || price === undefined) return null;
  const twinPrice = price - (report.attribution?.vsAutopilot?.price ?? Number.NaN);
  if (!Number.isFinite(twinPrice)) return null;
  const gap = price * shareRatio - twinPrice;
  if (Math.abs(gap) < MIN_PRICE_GAP_USD) return null;
  const given = (c as { readonly todayDollarFactor?: number }).todayDollarFactor;
  return { k: given !== undefined ? given : c.priceVsTwinUsdPerShare / gap, twinPrice };
}

/** The formula line; null without the share counts (results stored before they were kept). */
export function splitFormula(report: Pick<RoundReport, "attribution" | "score" | "scorecard">): string | null {
  const c = report.score.components;
  const sh = c?.shares;
  if (c === undefined || sh === undefined || sh.twinShares <= 0) return null;
  const score = report.score.valueCreatedUsdPerShare;
  const p = printedParts(c, score);
  const money = (v: number): string => formatUsdPerShare(v, { signed: true });
  const ratio = sh.youShares / sh.twinShares;
  const price = c.roundEnd?.priceUsd;
  const f = todayFactor(report, price, ratio);
  const counts = `your ${formatSharesM(sh.youShares)} shares ÷ your twin's ${formatSharesM(sh.twinShares)}`;
  let priceLeg: string;
  if (f === null || price === undefined) {
    priceLeg = `${SPLIT_LEG.price.label} = ${SPLIT_LEG.price.gloss} (${counts}) = ${money(p.price)}`;
  } else {
    const ratioText = ratio.toFixed(4), kText = f.k.toFixed(3);
    // Rebuilt from the figures as printed, so the sign says whether a reader's arithmetic lands on the printed cent.
    const rebuilt = (cents(price) / 100 * Number(ratioText) - cents(f.twinPrice) / 100) * Number(kText);
    const sign = cents(rebuilt) === cents(p.price) ? "=" : "≈";
    priceLeg = `${SPLIT_LEG.price.label} = (your price ${formatUsdPerShare(price)} × ${ratioText} [${counts}] − your twin's price ${formatUsdPerShare(f.twinPrice)}) × ${kText} [converts year-end dollars to today's dollars] ${sign} ${money(p.price)}`;
  }
  const reportedOnly = Math.round((p.price + p.cash) * 100) / 100;
  const cashLeg = `${SPLIT_LEG.cash.label} = your dividends and buybacks since the start, against your twin's, on the same scale = ${money(p.cash)}`;
  const ltLeg = `${SPLIT_LEG.lookThrough.label} = the official score ${money(score)} less the reported-only score ${money(reportedOnly)} = ${money(p.lookThrough)}`;
  const together = `The three add to ${money(score)}.`;
  const all = `For all shareholders together, multiply by the ${formatMetric(sh.simStartShares / 1e6, "index", { precision: 2 })}M shares at the start: about ${formatMetric((score * sh.simStartShares) / 1e6, "usdM", { precision: 0, signed: true })}.`;
  return `How the parts are built, ${READS_ON}: ${priceLeg}. ${cashLeg}. ${ltLeg}. ${together} ${all}`;
}

/** The formula as a paragraph; nothing without the share counts. */
export function SplitFormula({ report }: { readonly report: Pick<RoundReport, "attribution" | "score" | "scorecard"> }) {
  const text = splitFormula(report);
  return text === null ? null : <p className="gx-results-decomp-line gx-num" data-testid="results.split.formula">{text}</p>;
}
